/**
 * Die Rahmenansicht — das Bauteil, das eine Fremdoberfläche im Portal zeigt.
 *
 * ADR-040: Header und Navigation zeichnet ausschließlich die Shell,
 * Fremdoberflächen erscheinen darin eingebettet. Was dabei schiefgehen kann,
 * steht als Prüfrezept in `tests/ui/README.md`; dieses Bauteil ist die Seite,
 * die dort geprüft wird.
 *
 * **Ein blockierter Rahmen ist von einem langsamen nicht sicher zu
 * unterscheiden**, und das ist keine Nachlässigkeit, sondern eine Eigenschaft
 * der Browser: Sendet ein Dienst `X-Frame-Options: DENY`, feuert am iframe
 * trotzdem `load` — für die Fehlerseite des Browsers. Der Inhalt ist fremder
 * Herkunft und damit nicht lesbar. Es gibt kein Ereignis, das „ich wurde
 * blockiert" bedeutet.
 *
 * Daraus folgt der Aufbau hier, und er ist bewusst konservativ:
 *
 * - Der **Weg zur eigenen Adresse ist immer sichtbar**, nicht erst im
 *   Fehlerfall. Er kostet eine Zeile und erspart die unlösbare Erkennung.
 * - Dienste, die sich melden können, bekommen zusätzlich eine harte Aussage:
 *   Bleibt das Lebenszeichen aus, erscheint nach `fristMs` ein Hinweis. Für
 *   Dienste ohne dieses Protokoll — Bulwark meldet sich nicht — wird kein
 *   Alarm erzeugt, denn ein falscher Alarm bei jedem Öffnen wäre schlimmer als
 *   keiner.
 *
 * `03` verlangt für den Fehlerfall „lesbarer Hinweis mit Verweis auf die
 * eigene Adresse, **kein leerer Kasten**". Genau das leistet die immer
 * sichtbare Zeile, auch ohne Erkennung.
 */

import { useEffect, useRef, useState } from "react";

import { Hinweis } from "@hwc/ui";

import {
  dienstAdresse,
  herkunftVon,
  nachrichtPruefen,
} from "./ortprotokoll.js";

export interface RahmenansichtEigenschaften {
  /** Anzeigename des Dienstes, für Hinweis und Beschriftung. */
  readonly name: string;
  /** Basisadresse des Dienstes — ohne Ort. */
  readonly adresse: string;
  /** Ort im Dienst, den die Shell aus ihrer eigenen Adresse kennt. */
  readonly ort?: string | null;
  /** Meldet die Shell, wenn der Dienst einen neuen Ort mitteilt. */
  readonly beiOrt?: (ort: string) => void;
  /**
   * Ob dieser Dienst das Lebenszeichen `{typ:"bereit"}` sendet. Nur dann darf
   * ein ausbleibendes Lebenszeichen als Befund gelten.
   */
  readonly meldetSich?: boolean;
  /** Frist für das Lebenszeichen. */
  readonly fristMs?: number;
}

export function Rahmenansicht({
  name,
  adresse,
  ort = null,
  beiOrt,
  meldetSich = false,
  fristMs = 4000,
}: RahmenansichtEigenschaften) {
  const [bereit, setzeBereit] = useState(false);
  const [fristAbgelaufen, setzeFrist] = useState(false);
  const rahmen = useRef<HTMLIFrameElement | null>(null);

  const herkunft = herkunftVon(adresse);
  const ziel = dienstAdresse(adresse, ort);

  useEffect(() => {
    function beiNachricht(ereignis: MessageEvent) {
      const { nachricht } = nachrichtPruefen(
        ereignis.data, ereignis.origin, herkunft);
      if (!nachricht) return; // stillschweigend verwerfen: fremde Seiten reden viel
      if (nachricht.typ === "bereit") setzeBereit(true);
      if (nachricht.typ === "ort") beiOrt?.(nachricht.wert);
    }
    window.addEventListener("message", beiNachricht);
    return () => window.removeEventListener("message", beiNachricht);
  }, [herkunft, beiOrt]);

  useEffect(() => {
    if (!meldetSich) return;
    setzeBereit(false);
    setzeFrist(false);
    const uhr = window.setTimeout(() => setzeFrist(true), fristMs);
    return () => window.clearTimeout(uhr);
  }, [meldetSich, fristMs, ziel]);

  const stumm = meldetSich && fristAbgelaufen && !bereit;

  return (
    <section className="rahmenansicht">
      {stumm && (
        <Hinweis titel={name}>
          hat sich nicht gemeldet. Möglicherweise lässt der Dienst sich nicht
          einbetten.{" "}
          <a href={ziel} target="_blank" rel="noreferrer">
            In eigenem Tab öffnen
          </a>
        </Hinweis>
      )}
      <iframe
        ref={rahmen}
        className="rahmenansicht__rahmen"
        src={ziel}
        title={name}
        // Nur was der Editor tatsächlich braucht (ADR-040, Punkt 2 der drei
        // Ebenen). Jede weitere Erlaubnis wäre eine, die auch der Rahmen
        // eines fremden Dienstes bekäme.
        allow="fullscreen; clipboard-read; clipboard-write"
      />
      <p className="rahmenansicht__fuss">
        {name} läuft in einem Rahmen des Portals.{" "}
        <a href={ziel} target="_blank" rel="noreferrer">
          Eigene Adresse des Dienstes
        </a>
      </p>
    </section>
  );
}
